<script lang="ts">
const readyPreviewKeys = new Set<string>()
const READY_PREVIEW_KEY_LIMIT = 360

function rememberReadyPreviewKey(key: string) {
  readyPreviewKeys.add(key)

  while (readyPreviewKeys.size > READY_PREVIEW_KEY_LIMIT) {
    const oldestKey = readyPreviewKeys.values().next().value

    if (!oldestKey) {
      break
    }

    readyPreviewKeys.delete(oldestKey)
  }
}
</script>

<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue'
import { useToast } from '@/composables/useToast'
import { DISTRIBUTE_UNSUPPORTED_MESSAGE } from '@/constants/distribute'
import type { FeedCardVariant } from '@/utils/distribute/card-model'
import { resolveFeedCardModel } from '@/utils/distribute/card-model'
import { buildLandingUrl, openLanding } from '@/utils/distribute/navigation'
import CardChrome from '@/components/distribute-card-chrome'
import ImageCard from '@/components/distribute-image-card'
import RadioCard from '@/components/distribute-radio-card'
import VideoCard from '@/components/distribute-video-card'
import type { Emits, Props } from './type'

defineOptions({
  name: 'DistributeFeedCard',
})

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const toast = useToast()
const model = computed(() => props.model ?? resolveFeedCardModel(props.item))
const hasPreviewReady = shallowRef(false)
const hasReportedPreviewReady = shallowRef(false)
const previewReadyKey = computed(() => {
  const previewImagesKey = model.value.previewImages
    .map((material) =>
      [material.src, material.cover, material.urls?.medium].filter(Boolean).join(','),
    )
    .filter(Boolean)
    .join(';')

  return [
    props.item.id,
    model.value.kind,
    model.value.coverUrl,
    model.value.previewVideoCoverUrl,
    model.value.previewVideoUrl,
    previewImagesKey,
  ]
    .filter(Boolean)
    .join('|')
})
const previewVariant = computed<FeedCardVariant>(() => {
  switch (model.value.kind) {
    case 'pipe-video':
    case 'pipe-image':
    case 'unknown':
      return 'pipe'
    default:
      return 'media'
  }
})
const isVideoCard = computed(
  () =>
    model.value.kind === 'media-video' ||
    model.value.kind === 'media-mv' ||
    model.value.kind === 'pipe-video',
)
const isRadioCard = computed(() => model.value.kind === 'radio')

function openCard() {
  if (!hasPreviewReady.value) {
    return
  }

  if (props.requireLogin) {
    emit('login')
    return
  }

  if (!buildLandingUrl(props.item)) {
    toast.info(DISTRIBUTE_UNSUPPORTED_MESSAGE)
    return
  }

  openLanding(props.item)
}

function markPreviewReady() {
  if (previewReadyKey.value) {
    rememberReadyPreviewKey(previewReadyKey.value)
  }

  if (!hasPreviewReady.value) {
    hasPreviewReady.value = true
  }

  reportPreviewReady()
}

function reportPreviewReady() {
  if (hasReportedPreviewReady.value) {
    return
  }

  hasReportedPreviewReady.value = true
  emit('preview-ready')
}

watch(
  previewReadyKey,
  (key) => {
    hasPreviewReady.value = Boolean(key && readyPreviewKeys.has(key))
    hasReportedPreviewReady.value = false

    if (hasPreviewReady.value) {
      reportPreviewReady()
    }
  },
  { immediate: true },
)
</script>

<template>
  <article
    class="feed-card"
    :class="{
      'is-preview-loading': !hasPreviewReady,
      'is-preview-ready': hasPreviewReady,
    }"
    :style="{
      '--feed-card-loading-aspect': model.aspectRatio,
    }"
    :tabindex="hasPreviewReady ? 0 : -1"
    role="link"
    :aria-busy="!hasPreviewReady"
    :aria-hidden="!hasPreviewReady"
    @click="openCard"
    @keydown.enter.self="openCard"
    @keydown.space.self.prevent="openCard"
  >
    <div
      class="feed-card__content"
      :aria-hidden="!hasPreviewReady"
      :inert="hasPreviewReady ? undefined : true"
    >
      <RadioCard
        v-if="isRadioCard"
        :model="model"
        :priority="priority"
        :autoplay-active="autoplayActive"
        :require-login="requireLogin"
        @login="$emit('login')"
        @preview-ready="markPreviewReady"
      />
      <VideoCard
        v-else-if="isVideoCard"
        :model="model"
        :variant="previewVariant"
        :media-width="mediaWidth"
        :priority="priority"
        :autoplay-active="autoplayActive"
        :require-login="requireLogin"
        @login="$emit('login')"
        @preview-ready="markPreviewReady"
      />
      <ImageCard
        v-else
        :model="model"
        :variant="previewVariant"
        :media-width="mediaWidth"
        :priority="priority"
        :autoplay-active="autoplayActive"
        :require-login="requireLogin"
        @login="$emit('login')"
        @preview-ready="markPreviewReady"
      />
      <CardChrome :model="model" />
    </div>

    <div
      class="feed-card__loading-shell"
      :class="{ 'is-hidden': hasPreviewReady }"
      data-testid="feed-card-loading-shell"
      aria-hidden="true"
    >
      <div class="feed-card__loading-media" />
      <div class="feed-card__loading-copy">
        <span class="feed-card__loading-title" />
        <span class="feed-card__loading-author" />
      </div>
    </div>
  </article>
</template>

<style scoped>
.feed-card {
  position: relative;
  isolation: isolate;
  display: inline-flex;
  width: 100%;
  flex-direction: column;
  gap: 0;
  max-width: 400px;
  padding: 12px 12px 16px;
  border-radius: 16px;
  break-inside: avoid;
  vertical-align: top;
  cursor: default;
  opacity: 1;
  outline: none;
  pointer-events: none;
  --feed-card-skeleton-base: var(--app-skeleton-base);
  --feed-card-skeleton-line: var(--app-skeleton-line);
  --feed-card-skeleton-shine: var(--app-skeleton-shimmer);
}

.feed-card.is-preview-ready {
  cursor: pointer;
  pointer-events: auto;
}

.feed-card__content {
  position: relative;
  z-index: 1;
  display: inline-flex;
  width: 100%;
  flex-direction: column;
  gap: 0;
  opacity: 0;
  transition: opacity 0.22s ease;
}

.feed-card.is-preview-ready .feed-card__content {
  opacity: 1;
}

.feed-card__loading-shell {
  position: absolute;
  inset: 12px 12px 16px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 0;
  border-radius: 12px;
  opacity: 1;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.feed-card__loading-shell.is-hidden {
  opacity: 0;
}

.feed-card__loading-media {
  position: relative;
  overflow: hidden;
  aspect-ratio: var(--feed-card-loading-aspect);
  border-radius: 12px;
  background: var(--feed-card-skeleton-base);
}

.feed-card__loading-media::before,
.feed-card__loading-title::before,
.feed-card__loading-author::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    110deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0) 38%,
    var(--feed-card-skeleton-shine) 50%,
    rgba(255, 255, 255, 0) 62%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: translateX(-120%);
  animation: feed-card-skeleton-shine 1.9s ease-in-out infinite;
}

.feed-card__loading-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
}

.feed-card__loading-title,
.feed-card__loading-author {
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  background: var(--feed-card-skeleton-line);
}

.feed-card__loading-title {
  width: 82%;
  height: 18px;
}

.feed-card__loading-author {
  width: 36%;
  height: 16px;
}

.feed-card::before {
  content: '';
  position: absolute;
  inset: 4px;
  z-index: -1;
  border-radius: 20px;
  background-color: var(--distribute-hover-bg);
  opacity: 0;
  transform: scale(var(--app-interaction-media-rest-scale));
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.feed-card:hover::before {
  opacity: 1;
  transform: scale(var(--app-interaction-media-reveal-scale));
}

.feed-card:focus-visible::before {
  opacity: 1;
  transform: scale(var(--app-interaction-media-reveal-scale));
}

@keyframes feed-card-skeleton-shine {
  0% {
    transform: translateX(-120%);
  }

  52%,
  100% {
    transform: translateX(120%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .feed-card__content,
  .feed-card__loading-shell {
    transition: none;
  }

  .feed-card__loading-media::before,
  .feed-card__loading-title::before,
  .feed-card__loading-author::before {
    animation: none;
  }

  .feed-card::before {
    transition: opacity 0.18s ease;
  }

  .feed-card:hover::before {
    transform: scale(1);
  }
}
</style>
